import React, { useContext, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Hidden,
  IconButton,
  Typography,
  Tooltip,
  Chip,
} from '@material-ui/core';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { useQueryClient } from '@tanstack/react-query';
import MenuIcon from '@material-ui/icons/Menu';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import clsx from 'clsx';
import Logo from 'assets/logos/logo-white.png';
import MobileLogo from 'assets/logos/logo-white-mobile.png';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import UserContext from 'state/UserContext';
import ArrowBack from '@material-ui/icons/ArrowBackIos';
import useIsMobile from 'theme/useIsMobile';
import { Badge } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { userHasAccess } from 'models/Role';
import { useInboxCounts } from 'queries/useInboxCounts';

const useStyles = makeStyles((theme) => ({
  root: {
    boxShadow: 'none',
  },
  toolbar: {
    // position: 'fixed',
    // backgroundColor: theme.palette.primary.main,
    // zIndex: 10000,
    // width: '100%'
  },
  flexGrow: {
    flexGrow: 1,
  },
  logo: {
    height: '28px',
  },
  orgLabel: {
    marginLeft: theme.spacing(3),
    color: 'white',
    borderRadius: 4,
    borderColor: theme.palette.info.light,
  },
  userName: {
    marginRight: theme.spacing(2),
  },
  roleLabel: {
    borderRadius: 4,
    fontSize: '0.7rem',
    backgroundColor: theme.palette.primary.dark,
    color: 'inherit',
  },
  limitedRoleLabel: {
    fontSize: '0.7rem',
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.primary.main,
  },
  logoLink: {
    display: 'flex',
    alignItems: 'center',
  },
  backBtn: {
    marginRight: theme.spacing(2),
  },
  desktopMenuBtn: {
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(2),
  },
}));

interface Props {
  className?: string;
  sidebarIsOpen: boolean;
  onSidebarOpen: () => void;
  onSidebarClose?: () => void;
}

const Topbar = React.memo((props: Props) => {
  const { user, setUser } = useContext(UserContext);
  const { className, sidebarIsOpen, onSidebarOpen, onSidebarClose, ...rest } =
    props;
  const classes = useStyles();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [offline, setOffline] = useState(!navigator.onLine);
  // Resolved before the `!user` early return below, because the hook that
  // consumes it cannot be called conditionally.
  const canSeeInbox = Boolean(
    user &&
      userHasAccess(user, ['Administrator', 'ProjectManager', 'Supervisor'])
  );
  // Gated on the role rather than merely hidden. This mounts on every route
  // for every user and the counts endpoint is restricted to these same three
  // roles, so leaving the fetch on would have a technician's session polling a
  // rejected request every minute for as long as they stayed logged in -
  // not rendering the bell does not stop the polling.
  const { data: inboxCounts } = useInboxCounts(canSeeInbox);

  window.addEventListener('offline', () => setOffline(true));
  window.addEventListener('online', () => setOffline(false));

  if (!user) {
    return null;
  }
  // Later is excluded on purpose: those items were already deliberately
  // deferred by the user snoozing them, so the bell shouldn't re-alarm on
  // them.
  const unreadCount = (inboxCounts?.primary ?? 0) + (inboxCounts?.other ?? 0);

  const clearCache = () => {
    queryClient.clear();
    localStorage.removeItem('token');
  };

  const logout = () => {
    clearCache();
    setUser(null);
    navigate('/login');
  };

  const goBack = () => {
    navigate(-1);
  };

  return (
    <AppBar {...rest} className={clsx(classes.root, className)}>
      <Toolbar className={classes.toolbar}>
        {isMobile && (
          <IconButton
            size="small"
            color="inherit"
            className={classes.backBtn}
            onClick={goBack}>
            <ArrowBack color="inherit" fontSize="small" />
          </IconButton>
        )}
        <Hidden mdDown>
          <IconButton
            color="inherit"
            onClick={sidebarIsOpen ? onSidebarClose : onSidebarOpen}
            className={classes.desktopMenuBtn}>
            <MenuIcon />
          </IconButton>
        </Hidden>
        <RouterLink to="/" className={classes.logoLink}>
          <img
            alt="Logo"
            className={classes.logo}
            src={isMobile ? MobileLogo : Logo}
          />
        </RouterLink>
        <Chip
          variant="outlined"
          className={classes.orgLabel}
          label={user.__organization__?.name}
        />
        <div className={classes.flexGrow} />
        <Hidden mdDown>
          <Typography
            variant="button"
            color="inherit"
            className={classes.userName}>
            {user.firstName} {user.lastName}
          </Typography>

          <Chip
            label={
              user.role === 'ProjectManager' ? 'Project Coordinator' : user.role
            }
            size="small"
            className={classes.roleLabel}
          />

          {canSeeInbox && (
            <Tooltip title="Inbox">
              <IconButton
                color="inherit"
                onClick={() => navigate('/inbox')}
                data-cy={'inbox-icon-btn'}>
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Your Profile">
            <IconButton
              color="inherit"
              onClick={() => navigate(`/users/profile/${user.id}`)}>
              <Badge
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: !offline ? 'lightgreen' : 'red',
                  },
                }}
                badgeContent=" "
                variant="dot">
                <AccountCircleIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title="Log out">
            <IconButton
              color="inherit"
              onClick={logout}
              data-cy={'log-out-btn'}>
              <ExitToAppIcon />
            </IconButton>
          </Tooltip>
        </Hidden>
        <Hidden lgUp>
          <IconButton
            color="inherit"
            onClick={onSidebarOpen}
            data-cy={'side-bar-icon'}>
            <MenuIcon />
          </IconButton>
        </Hidden>
      </Toolbar>
    </AppBar>
  );
});

export default Topbar;
